import { useMemo } from 'react';
import { css } from '@emotion/react';
import { QuickFillHooks } from '@frontend/api-quick-fill';
import { useAiScheduleAccess } from '@frontend/ai-receptionist-settings';
import { AnimatedBadge } from '@frontend/components';
import { useDevToolsStore } from '@frontend/devtools';
import { useTranslation } from '@frontend/i18n';
import { useGetWeaveEnhancedInsuranceVerificationCustomizationFlagDetails } from '@frontend/insurance-verification';
import { useSchedulingLocationInfo } from '@frontend/schedule';
import { useScheduleActivityAccess } from '@frontend/schedule-helpers';
import { useScopedAppFlagStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Truncated } from '@frontend/design-system';
import { createNavItem, createSubNavItems } from '../nav-item-utils';
import { featureDefinition as scheduleFeatureDefinition } from './feature-definitions';

export const useScheduleNavItem = () => {
  const { t } = useTranslation('primaryNav');
  const { getFeatureFlagValue } = useScopedAppFlagStore();

  const isDebugModeOn = useDevToolsStore((state) => state.options.isDebugModeOn);
  const { getOnlineSchedulingReadyLocationIds } = useSchedulingLocationInfo();
  const { hasQuickFillFeature } = QuickFillHooks.useQuickFillFeatureDetails();
  const { canViewBridgelineScheduleSettings } = useAiScheduleAccess();
  const { isFeatureHiddenInAllLocation } = useGetWeaveEnhancedInsuranceVerificationCustomizationFlagDetails();
  const { isScheduleActivityEnabled } = useScheduleActivityAccess();
  const isEligibilityListViewFFEnabled = getFeatureFlagValue('insurance-eligibility-list-view');
  const onlineSchedulingReadyLocationIds = getOnlineSchedulingReadyLocationIds();
  const isOnlineSchedulingEnabledForAnyLocation = onlineSchedulingReadyLocationIds.length > 0;
  const hasAccessToEligibilityList = isEligibilityListViewFFEnabled && !isFeatureHiddenInAllLocation;
  const scheduleItems = scheduleFeatureDefinition.items;

  const subNavItems = useMemo(
    () =>
      createSubNavItems([
        {
          ...scheduleItems['/calendar'],
          label: t('Calendar'),
          relativePath: '/calendar',
        },
        {
          ...scheduleItems['/availability'],
          label: t('Availability Finder'),
          relativePath: '/availability',
          trackingId: 'nav-schedule-availability',
          show: canViewBridgelineScheduleSettings,
          description: t('Preview the schedule for a selected service.'),
        },
        {
          ...scheduleItems['/eligibility-list'],
          label: (
            <div css={insuranceEligibilityLabelStyles}>
              <Truncated>{t('Insurance Eligibility')}</Truncated>
              <AnimatedBadge label={t('New')} />
            </div>
          ),
          relativePath: '/eligibility-list',
          show: hasAccessToEligibilityList,
        },
        {
          ...scheduleItems['/schedule-activity'],
          label: t('Schedule Activity'),
          relativePath: '/schedule-activity',
          show: isOnlineSchedulingEnabledForAnyLocation && isScheduleActivityEnabled,
        },
        {
          ...scheduleItems['/request-history'],
          label: t('Request History'),
          relativePath: '/request-history',
          show: isOnlineSchedulingEnabledForAnyLocation && !isScheduleActivityEnabled,
        },
        {
          ...scheduleItems['/quickfill-history'],
          label: t('Waitlist History'),
          relativePath: '/quickfill-history',
          show: hasQuickFillFeature,
        },
        {
          ...scheduleItems['/debug'],
          label: t('Debug'),
          relativePath: '/debug',
          show: isDebugModeOn,
        },
      ]),
    [
      t,
      isOnlineSchedulingEnabledForAnyLocation,
      isScheduleActivityEnabled,
      hasQuickFillFeature,
      isDebugModeOn,
      canViewBridgelineScheduleSettings,
      hasAccessToEligibilityList,
      scheduleItems,
    ]
  );

  return useMemo(
    () =>
      createNavItem('schedule', {
        iconName: 'calendar',
        label: t('Schedule'),
        root: '/schedule',
        subNavItems,
        settings: { path: '/schedule', label: '' },
      }),
    [t, subNavItems]
  );
};
const insuranceEligibilityLabelStyles = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(0.5)};
`;
